import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import AudioPromptPicker from "@/components/audio/AudioPromptPicker";
import { CallFlowPrompt } from "@/model/interface/CallFlow";
import { useGetOwnMembershipQuery, useUpdateOwnVoicemailGreetingMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { toast } from "sonner";

function greetingFrom(text?: string | null, audioClipId?: string | null): CallFlowPrompt {
  if (audioClipId) {
    return { mode: "PLAY", audioClipId };
  }
  return { mode: "SAY", text: text ?? "" };
}

export default function VoicemailGreetingCard() {
  const { data: membership, isLoading } = useGetOwnMembershipQuery();
  const [updateGreeting, { isLoading: isSaving }] = useUpdateOwnVoicemailGreetingMutation();
  const [greeting, setGreeting] = useState<CallFlowPrompt>({ mode: "SAY", text: "" });

  async function save() {
    try {
      await updateGreeting({
        voicemailGreetingText: greeting.mode === "SAY" ? greeting.text : null,
        voicemailGreetingAudioClipId: greeting.mode === "PLAY" ? greeting.audioClipId : null,
      }).unwrap();
      toast.success("Voicemail greeting saved");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to save your voicemail greeting"));
    }
  }

  async function clear() {
    setGreeting({ mode: "SAY", text: "" });
    try {
      await updateGreeting({ voicemailGreetingText: null, voicemailGreetingAudioClipId: null }).unwrap();
      toast.success("Voicemail greeting removed");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to remove your voicemail greeting"));
    }
  }

  useEffect(() => {
    if (membership) {
      setGreeting(greetingFrom(membership.voicemailGreetingText, membership.voicemailGreetingAudioClipId));
    }
  }, [membership]);

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  const hasGreeting = Boolean(membership?.voicemailGreetingText || membership?.voicemailGreetingAudioClipId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Voicemail greeting</CardTitle>
        <CardDescription>
          Played when a call flow sends a caller to your voicemail and is set to use your own greeting.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <AudioPromptPicker
          label="Your greeting"
          placeholder="Hi, you have reached my voicemail. Leave a message and I will get back to you."
          value={greeting}
          onChange={setGreeting}
        />

        <div className="flex flex-wrap gap-3">
          <Button onClick={save} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save greeting"}
          </Button>
          {hasGreeting && (
            <Button variant="outline" onClick={clear} disabled={isSaving}>
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
